<!--
  MentorApplicationStatusModal — модалка статуса заявки наставника.

  Открывается по клику на статус-чип «Наставник» в read-only-фазе (в первую
  очередь «на проверке» / pending, когда пользователь не может ничего сделать —
  только понять, что происходит). Самодостаточна и презентационна: принимает
  фазу и причину отказа props'ами, ничего не фетчит. Teleport в body — чтобы
  не зависеть от overflow/z-index шапки.

  Mobile-first: на десктопе — центрированная карточка; на узких экранах
  (≤640px) — bottom-sheet (выезжает снизу, на всю ширину, со скруглённым
  верхом, прокручиваемый).

  A11y: role="dialog" + aria-modal + aria-labelledby; Esc и клик по подложке
  закрывают; фокус ставится на главную кнопку при открытии; анимации
  отключаются при prefers-reduced-motion.
-->
<template>
    <Teleport to="body">
        <Transition name="mas-fade">
            <div
                v-if="modelValue"
                class="mas-overlay"
                data-testid="mentor-status-modal"
                @click.self="close"
                @keydown.esc.stop.prevent="close"
            >
                <Transition name="mas-pop" appear>
                    <div
                        v-if="modelValue"
                        class="mas-card"
                        role="dialog"
                        aria-modal="true"
                        :aria-labelledby="titleId"
                        tabindex="-1"
                    >
                        <button
                            type="button"
                            class="mas-close"
                            aria-label="Закрыть"
                            data-testid="mentor-status-modal-close"
                            @click="close"
                        >
                            <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                                <path
                                    d="M6 6l12 12M18 6L6 18"
                                    fill="none"
                                    stroke="currentColor"
                                    stroke-width="2"
                                    stroke-linecap="round"
                                />
                            </svg>
                        </button>

                        <div class="mas-icon" :class="`mas-icon--${tone}`" aria-hidden="true">
                            <!-- Часы для pending, галочка для approved, и т.п. -->
                            <svg
                                v-if="tone === 'pending'"
                                viewBox="0 0 24 24"
                                width="26"
                                height="26"
                            >
                                <circle
                                    cx="12"
                                    cy="12"
                                    r="9"
                                    fill="none"
                                    stroke="currentColor"
                                    stroke-width="2"
                                />
                                <path
                                    d="M12 7.5V12l3 2"
                                    fill="none"
                                    stroke="currentColor"
                                    stroke-width="2"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                />
                            </svg>
                            <svg
                                v-else-if="tone === 'success'"
                                viewBox="0 0 24 24"
                                width="26"
                                height="26"
                            >
                                <path
                                    d="M5 13l4 4L19 7"
                                    fill="none"
                                    stroke="currentColor"
                                    stroke-width="2.5"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                />
                            </svg>
                            <svg v-else viewBox="0 0 24 24" width="26" height="26">
                                <path
                                    d="M12 8v5M12 16.5v.5"
                                    fill="none"
                                    stroke="currentColor"
                                    stroke-width="2"
                                    stroke-linecap="round"
                                />
                                <circle
                                    cx="12"
                                    cy="12"
                                    r="9"
                                    fill="none"
                                    stroke="currentColor"
                                    stroke-width="2"
                                />
                            </svg>
                        </div>

                        <h2 :id="titleId" class="mas-title">{{ title }}</h2>
                        <p class="mas-subtitle">{{ subtitle }}</p>

                        <!-- Мини-таймлайн заявки: подана → проверка → решение. -->
                        <ol class="mas-timeline" aria-hidden="true">
                            <li
                                v-for="(t, i) in timeline"
                                :key="t.key"
                                class="mas-tl-step"
                                :class="[`is-${t.state}`, { 'has-line': i > 0 }]"
                            >
                                <span
                                    v-if="i > 0"
                                    class="mas-tl-line"
                                    :class="{ 'is-filled': timeline[i - 1].state === 'done' }"
                                />
                                <span class="mas-tl-node">
                                    <svg
                                        v-if="t.state === 'done'"
                                        viewBox="0 0 24 24"
                                        width="12"
                                        height="12"
                                    >
                                        <path
                                            d="M5 13l4 4L19 7"
                                            fill="none"
                                            stroke="currentColor"
                                            stroke-width="2.5"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                        />
                                    </svg>
                                    <span v-else class="mas-tl-dot" />
                                </span>
                                <span class="mas-tl-label">{{ t.label }}</span>
                            </li>
                        </ol>

                        <p class="mas-body">{{ bodyText }}</p>

                        <div v-if="note" class="mas-note" :class="`mas-note--${tone}`">
                            {{ note }}
                        </div>

                        <button
                            ref="primaryBtn"
                            type="button"
                            class="mas-primary"
                            data-testid="mentor-status-modal-ok"
                            @click="close"
                        >
                            Понятно
                        </button>
                    </div>
                </Transition>
            </div>
        </Transition>
    </Teleport>
</template>

<script setup lang="ts">
import { computed, ref, watch, nextTick } from 'vue';
import type { Phase } from '@/composables/useMentorApplicationStatus';
import { getRejectionTitle } from '@/shared/lib/mentorRejection';

interface Props {
    modelValue: boolean;
    phase: Phase;
    rejectionReason?: string;
}
const props = withDefaults(defineProps<Props>(), { rejectionReason: '' });
const emit = defineEmits<{ (e: 'update:modelValue', v: boolean): void }>();

const primaryBtn = ref<HTMLButtonElement | null>(null);
const titleId = `mas-title-${Math.random().toString(36).slice(2, 9)}`;

function close(): void {
    emit('update:modelValue', false);
}

// Фокус на главную кнопку при открытии (a11y).
watch(
    () => props.modelValue,
    async (open) => {
        if (open) {
            await nextTick();
            primaryBtn.value?.focus();
        }
    },
);

// ── Контент по фазе (основное — pending) ──────────────────────────────

type Tone = 'pending' | 'success' | 'info';
type TlState = 'done' | 'current' | 'upcoming';

const tone = computed<Tone>(() => {
    if (props.phase === 'approved') return 'success';
    if (props.phase === 'pending' || props.phase === 'changes_requested') return 'pending';
    return 'info';
});

const title = computed<string>(() => {
    switch (props.phase) {
        case 'pending':
            return 'Заявка на проверке';
        case 'changes_requested':
            return 'Нужны правки заявки';
        case 'approved':
            return 'Вы — наставник';
        case 'rejected':
            return 'Заявка отклонена';
        case 'draft':
            return 'Заявка не отправлена';
        default:
            return 'Статус заявки наставника';
    }
});

const subtitle = computed<string>(() => {
    switch (props.phase) {
        case 'pending':
            return 'Мы проверяем вашу заявку наставника';
        case 'changes_requested':
            return 'Модератор вернул заявку на доработку';
        case 'approved':
            return 'Профиль наставника подтверждён';
        case 'rejected':
            return 'К сожалению, заявку не одобрили';
        case 'draft':
            return 'Вы начали, но ещё не отправили заявку';
        default:
            return '';
    }
});

/**
 * Человекочитаемая причина отказа. Контейнер прокидывает сырой
 * `rejection_reason` (TextChoices code, напр. "low_quality"); мапим его через
 * тот же shared-lookup, что и баннер на /become-mentor и dashboard-карточка,
 * чтобы не печатать машинный код. Неизвестный код (или уже человекочитаемая
 * строка от старого вызывающего кода) проходит как есть — fallback не ломает
 * существующих потребителей.
 */
const rejectionReasonText = computed<string>(() => {
    const raw = props.rejectionReason?.trim();
    if (!raw) return '';
    return getRejectionTitle(raw) || raw;
});

const bodyText = computed<string>(() => {
    switch (props.phase) {
        case 'pending':
            return 'Обычно проверка занимает до 3 рабочих дней. Как только примем решение — пришлём уведомление, ничего делать не нужно.';
        case 'changes_requested':
            return 'Откройте заявку, дополните недостающие поля и отправьте её снова.';
        case 'approved':
            return 'Теперь вы можете проводить сессии. Привяжите карту для выплат, чтобы получать за них оплату.';
        case 'rejected':
            return rejectionReasonText.value
                ? `Причина: ${rejectionReasonText.value}. Поправьте профиль и подайте заявку заново.`
                : 'Поправьте профиль и подайте заявку заново.';
        case 'draft':
            return 'Вернитесь к заявке и заполните её до конца, чтобы отправить на модерацию.';
        default:
            return '';
    }
});

const note = computed<string>(() => {
    if (
        props.phase === 'pending' ||
        props.phase === 'changes_requested' ||
        props.phase === 'draft'
    ) {
        return 'Пока заявка не одобрена, платные сессии недоступны — карту для выплат можно будет привязать после подтверждения.';
    }
    return '';
});

/** Мини-таймлайн «подана → проверка → решение», состояние по фазе. */
const timeline = computed<Array<{ key: string; label: string; state: TlState }>>(() => {
    const submitted: TlState = props.phase === 'draft' ? 'current' : 'done';
    const review: TlState =
        props.phase === 'pending' || props.phase === 'changes_requested'
            ? 'current'
            : props.phase === 'approved' || props.phase === 'rejected'
              ? 'done'
              : 'upcoming';
    const decision: TlState =
        props.phase === 'approved' || props.phase === 'rejected' ? 'done' : 'upcoming';
    return [
        { key: 'submitted', label: 'Подана', state: submitted },
        { key: 'review', label: 'Проверка', state: review },
        {
            key: 'decision',
            label:
                props.phase === 'approved'
                    ? 'Одобрена'
                    : props.phase === 'rejected'
                      ? 'Отклонена'
                      : 'Решение',
            state: decision,
        },
    ];
});
</script>

<style scoped>
/* ── Подложка ── */
.mas-overlay {
    position: fixed;
    inset: 0;
    z-index: 1000;
    background: rgba(20, 16, 38, 0.55);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
}

/* ── Карточка ── */
.mas-card {
    position: relative;
    width: 100%;
    max-width: 420px;
    background: #ffffff;
    border-radius: 20px;
    padding: 28px 24px 24px;
    box-shadow: 0 24px 60px rgba(20, 16, 38, 0.28);
    outline: none;
    text-align: center;
}

.mas-close {
    position: absolute;
    top: 14px;
    right: 14px;
    width: 32px;
    height: 32px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: none;
    background: transparent;
    color: #9a97ad;
    border-radius: 8px;
    cursor: pointer;
}
.mas-close:hover {
    background: #f3f2f9;
    color: #5b5572;
}
.mas-close:focus-visible {
    outline: 2px solid #7b65f7;
    outline-offset: 2px;
}

/* Иконка-кружок */
.mas-icon {
    width: 56px;
    height: 56px;
    margin: 4px auto 14px;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}
.mas-icon--pending {
    background: #fff5e6;
    color: #c2740a;
}
.mas-icon--success {
    background: #e9f8ef;
    color: #1c9d5b;
}
.mas-icon--info {
    background: #eef0f6;
    color: #5b5572;
}

.mas-title {
    margin: 0 0 4px;
    font-size: 20px;
    font-weight: 800;
    color: #2a2037;
    letter-spacing: -0.01em;
}
.mas-subtitle {
    margin: 0 0 20px;
    font-size: 14px;
    color: #6b6580;
    line-height: 1.4;
}

/* ── Таймлайн (горизонтальный на десктопе) ── */
.mas-timeline {
    display: flex;
    align-items: flex-start;
    list-style: none;
    margin: 0 0 20px;
    padding: 0;
}
.mas-tl-step {
    position: relative;
    flex: 1 1 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 7px;
    text-align: center;
}
.mas-tl-line {
    position: absolute;
    top: 13px;
    transform: translateY(-50%);
    left: calc(-50% + 16px);
    right: calc(50% + 16px);
    height: 2px;
    background: #e7e5f2;
    border-radius: 2px;
}
.mas-tl-line.is-filled {
    background: #6048e5;
}
.mas-tl-node {
    position: relative;
    z-index: 1;
    width: 26px;
    height: 26px;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: #fff;
    border: 1.5px solid #d9d7ea;
    color: #fff;
}
.mas-tl-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #c4c1d6;
}
.mas-tl-step.is-done .mas-tl-node {
    background: #6048e5;
    border-color: #6048e5;
}
.mas-tl-step.is-current .mas-tl-node {
    border-color: #c2740a;
    box-shadow: 0 0 0 3px rgba(194, 116, 10, 0.16);
}
.mas-tl-step.is-current .mas-tl-dot {
    background: #c2740a;
}
.mas-tl-label {
    font-size: 12px;
    color: #9a97ad;
}
.mas-tl-step.is-done .mas-tl-label {
    color: #5b5572;
}
.mas-tl-step.is-current .mas-tl-label {
    color: #c2740a;
    font-weight: 700;
}

.mas-body {
    margin: 0 0 16px;
    font-size: 14px;
    line-height: 1.5;
    color: #434966;
}

.mas-note {
    text-align: left;
    font-size: 13px;
    line-height: 1.45;
    border-radius: 12px;
    padding: 12px 14px;
    margin: 0 0 20px;
}
.mas-note--pending {
    background: #fff8ec;
    color: #8a5a08;
    border: 1px solid #f6e3c2;
}
.mas-note--success {
    background: #eef9f2;
    color: #1c7a48;
    border: 1px solid #cdebd9;
}
.mas-note--info {
    background: #f5f5fa;
    color: #5b5572;
    border: 1px solid #e7e5f2;
}

.mas-primary {
    width: 100%;
    padding: 13px 16px;
    border: none;
    border-radius: 12px;
    background: #6048e5;
    color: #fff;
    font-size: 15px;
    font-weight: 700;
    cursor: pointer;
    font-family: inherit;
    transition: background 160ms ease;
}
.mas-primary:hover {
    background: #5238d8;
}
.mas-primary:focus-visible {
    outline: 2px solid #2a2037;
    outline-offset: 2px;
}

/* ── Анимации ── */
.mas-fade-enter-active,
.mas-fade-leave-active {
    transition: opacity 180ms ease;
}
.mas-fade-enter-from,
.mas-fade-leave-to {
    opacity: 0;
}
.mas-pop-enter-active {
    transition:
        transform 220ms cubic-bezier(0.16, 1, 0.3, 1),
        opacity 220ms ease;
}
.mas-pop-enter-from {
    transform: translateY(8px) scale(0.98);
    opacity: 0;
}

/* ── Mobile: bottom-sheet ── */
@media (max-width: 640px) {
    .mas-overlay {
        align-items: flex-end;
        padding: 0;
    }
    .mas-card {
        max-width: none;
        border-radius: 20px 20px 0 0;
        padding: 24px 18px calc(20px + env(safe-area-inset-bottom, 0px));
        max-height: 92vh;
        overflow-y: auto;
    }
    .mas-pop-enter-from {
        transform: translateY(100%);
        opacity: 1;
    }
    .mas-tl-label {
        font-size: 11px;
    }
}

@media (prefers-reduced-motion: reduce) {
    .mas-fade-enter-active,
    .mas-fade-leave-active,
    .mas-pop-enter-active {
        transition: none;
    }
}
</style>
